import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import css from './PendingValueComparison.module.css';

// Helper to compare values including arrays and objects
const areValuesEqual = (a, b) => {
  // Handle null/undefined
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  
  // Handle arrays
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((val, i) => val === sortedB[i]);
  }
  
  // Handle objects (shallow comparison)
  if (typeof a === 'object' && typeof b === 'object') {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    return keysA.every(key => a[key] === b[key]);
  }
  
  // Primitive comparison
  return a === b;
};

// Helper to format array values for display
const formatDisplayValue = (value, formatFn) => {
  if (formatFn) return formatFn(value);
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object' && value !== null) return JSON.stringify(value);
  return value;
};

/**
 * Component to display a comparison between published value and pending approval value
 * Shows when a field has pending changes awaiting moderation approval
 */
const PendingValueComparison = ({
  currentValue,
  pendingValue,
  fieldLabel = null,
  className = null,
  formatValue = null, // optional function to format complex values (e.g., price, dates)
}) => {

  // Don't render if no pending value or if values are the same
  if (pendingValue == null || areValuesEqual(currentValue, pendingValue)) {
    return null;
  }

  const classes = classNames(css.root, className);
  const displayCurrent = formatDisplayValue(currentValue, formatValue);
  const displayPending = formatDisplayValue(pendingValue, formatValue);

  return (
    <div className={classes}>
      <div className={css.label}>
        {fieldLabel && <span className={css.fieldLabel}>{fieldLabel}</span>}
        <FormattedMessage id="PendingValueComparison.pendingApproval" />
      </div>
      <div className={css.comparison}>
        <div className={css.currentValue}>
          <span className={css.valueLabel}>
            <FormattedMessage id="PendingValueComparison.currentPublished" />
          </span>
          <span className={css.value}>{displayCurrent || '—'}</span>
        </div>
        <div className={css.pendingValue}>
          <span className={css.valueLabel}>
            <FormattedMessage id="PendingValueComparison.pendingApproval" />
          </span>
          <span className={css.value}>{displayPending || '—'}</span>
        </div>
      </div>
      <div className={css.info}>
        <FormattedMessage id="PendingValueComparison.editingPendingValue" />
      </div>
    </div>
  );
};

PendingValueComparison.propTypes = {
  currentValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.object,
    PropTypes.array,
  ]),
  pendingValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.object,
    PropTypes.array,
  ]),
  fieldLabel: PropTypes.string,
  className: PropTypes.string,
  formatValue: PropTypes.func,
};

export default PendingValueComparison;
